<template>
  <section
    class="box mt-8 p-6 team-reset-danger"
    aria-labelledby="reset-heading"
  >
    <h3 id="reset-heading" class="text-xl font-medium team-reset-warning">
      Danger zone
    </h3>
    <h4 class="text-lg font-medium mt-3">Reset all active teams</h4>
    <p class="mt-2">
      Prepare teams for a new event year using the same reset as each team's
      settings. This archives their history, deactivates them and resets their
      figures and progress. Teams stay inactive until they are reactivated. This
      cannot be undone here.
    </p>
    <p class="mt-2">
      Save the new event year above first. History is archived under the year
      before the saved event year. This process may take a while; you can leave
      this page and return to check its progress.
    </p>

    <p v-if="error" class="mt-4 team-reset-warning" role="alert">{{ error }}</p>
    <button
      v-if="error"
      type="button"
      class="btn btn-outline-secondary mt-3"
      :disabled="loading || submitting"
      @click="refresh"
    >
      Refresh status
    </button>
    <p v-if="!status && loading" class="mt-4" role="status">
      Loading team reset status…
    </p>

    <div v-if="job" class="mt-5 p-4 rounded border" aria-live="polite">
      <h4 class="font-medium">
        Reset for {{ job.eventYear }} — {{ statusLabel }}
      </h4>
      <p class="mt-2">
        {{ processed }} of {{ job.total }} teams processed:
        {{ job.succeeded }} reset, {{ job.skipped }} skipped,
        {{ job.failed }} failed.
      </p>
      <progress
        class="w-full mt-3"
        :value="processed"
        :max="job.total || 1"
        aria-label="Team reset progress"
      />
      <p v-if="job.skipped" class="mt-2">
        Skipped teams were already inactive or had been removed.
      </p>
      <p v-if="job.message" class="mt-2">{{ job.message }}</p>
      <p v-if="busy" class="mt-2">
        Running in the background. Progress is saved automatically, including
        across server restarts.
      </p>
      <ul v-if="job.failures.length" class="list-disc ml-5 mt-3">
        <li v-for="failure in job.failures" :key="failure.teamId">
          {{ failure.name || failure.teamId }}: {{ failure.error }}
        </li>
      </ul>
    </div>

    <div v-if="status && !busy && !review" class="mt-5">
      <p v-if="!canResume">
        {{ status.activeTeamCount }} active teams · Saved event year
        {{ status.eventYear }} · Archive year {{ status.archiveYear }}
      </p>
      <button
        type="button"
        class="btn team-reset-button mt-3"
        :disabled="
          loading ||
            submitting ||
            !!error ||
            (!canResume && !status.activeTeamCount)
        "
        @click="prepare(canResume ? 'resume' : 'start')"
      >
        {{
          canResume
            ? "Review remaining teams"
            : "Review reset of all active teams"
        }}
      </button>
    </div>

    <form
      v-if="review"
      class="mt-5 p-4 rounded border"
      @submit.prevent="submit"
    >
      <h4 class="font-medium team-reset-warning">Confirm team reset</h4>
      <p class="mt-2">
        {{ review.action === "resume" ? "Continue resetting" : "Reset" }}
        <strong>{{ review.count }} teams</strong> for event year
        <strong>{{ review.eventYear }}</strong
        >. Their previous totals and team history will be archived as
        <strong>{{ review.archiveYear }}</strong
        >. Each team will be deactivated and its progress reset.
      </p>
      <p v-if="review.action === 'resume'" class="mt-2">
        Successfully reset teams will not be reset again. Only unfinished or
        failed teams from the original run will be processed.
      </p>
      <label for="team-reset-confirmation" class="block mt-4">
        To confirm, type <strong>{{ review.confirmation }}</strong>
      </label>
      <input
        id="team-reset-confirmation"
        ref="confirmationInput"
        v-model="confirmation"
        type="text"
        class="form-control mt-2"
        autocomplete="off"
        :disabled="submitting"
      />
      <label class="flex items-start mt-4">
        <input
          v-model="acknowledged"
          type="checkbox"
          class="form-check-input mr-2 mt-1"
          :disabled="submitting"
        />
        <span
          >I understand these teams will be deactivated and their progress
          reset.</span
        >
      </label>
      <div class="flex flex-wrap gap-3 mt-4">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="submitting"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn team-reset-button"
          :disabled="!confirmed || submitting || busy"
        >
          {{ submitting ? "Starting…" : "Confirm and run team reset" }}
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref
} from "vue";
import {
  getTeamResetStatus,
  resumeTeamReset,
  startTeamReset
} from "@/api/event-settings";

export default defineComponent({
  setup() {
    const status = ref(null);
    const error = ref("");
    const loading = ref(false);
    const submitting = ref(false);
    const review = ref(null);
    const confirmation = ref("");
    const acknowledged = ref(false);
    const confirmationInput = ref(null);
    const job = computed(() => status.value && status.value.job);
    const busy = computed(
      () => !!job.value && ["queued", "running"].includes(job.value.status)
    );
    const canResume = computed(
      () =>
        !!job.value &&
        ["interrupted", "completed_with_errors"].includes(job.value.status)
    );
    const processed = computed(() =>
      job.value ? job.value.total - job.value.pending : 0
    );
    const statusLabel = computed(() => {
      const labels = {
        queued: "Waiting to start",
        running: "In progress",
        interrupted: "Needs attention",
        completed: "Complete",
        completed_with_errors: "Complete with failures"
      };
      return job.value ? labels[job.value.status] : "";
    });
    const confirmed = computed(
      () =>
        !!review.value &&
        confirmation.value === review.value.confirmation &&
        acknowledged.value
    );

    const refresh = async () => {
      if (loading.value) return false;
      loading.value = true;
      try {
        status.value = await getTeamResetStatus();
        error.value = "";
        return true;
      } catch (err) {
        error.value = err.message;
        return false;
      } finally {
        loading.value = false;
      }
    };

    const cancel = () => {
      review.value = null;
      confirmation.value = "";
      acknowledged.value = false;
    };

    const prepare = async action => {
      cancel();
      if (!(await refresh()) || busy.value) return;
      if (action === "resume" && !canResume.value) return;
      if (action === "start" && canResume.value) return;
      if (action === "start" && !status.value.activeTeamCount) return;
      const eventYear =
        action === "resume" ? job.value.eventYear : status.value.eventYear;
      review.value = {
        action,
        eventYear,
        archiveYear: String(Number(eventYear) - 1),
        count:
          action === "resume"
            ? job.value.pending + job.value.failed
            : status.value.activeTeamCount,
        confirmation: `RESET ALL TEAMS FOR ${eventYear}`,
        runId: job.value && job.value.runId
      };
      await nextTick();
      confirmationInput.value.focus();
    };

    const submit = async () => {
      if (!confirmed.value || submitting.value || busy.value) return;
      submitting.value = true;
      try {
        const payload = { confirmation: confirmation.value };
        const result =
          review.value.action === "resume"
            ? await resumeTeamReset({ ...payload, runId: review.value.runId })
            : await startTeamReset({
                ...payload,
                eventYear: review.value.eventYear,
                activeTeamCount: review.value.count
              });
        status.value.job = result;
        cancel();
        error.value = "";
      } catch (err) {
        cancel();
        await refresh();
        error.value = err.message;
      } finally {
        submitting.value = false;
      }
    };

    let poll;
    onMounted(() => {
      refresh();
      poll = setInterval(() => {
        if (!review.value && !submitting.value) refresh();
      }, 5000);
    });
    onUnmounted(() => clearInterval(poll));

    return {
      status,
      error,
      loading,
      submitting,
      review,
      confirmation,
      acknowledged,
      confirmationInput,
      job,
      busy,
      canResume,
      processed,
      statusLabel,
      confirmed,
      refresh,
      cancel,
      prepare,
      submit
    };
  }
});
</script>

<style scoped>
.team-reset-danger {
  border: 2px solid #c51d5c;
}
.team-reset-warning {
  color: #c51d5c;
}
.team-reset-button {
  color: white;
  background: #c51d5c;
  border-color: #c51d5c;
}
.team-reset-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
